import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Loader2, Trash2 } from 'lucide-react';
import {
  GapVerdict,
  TruthBeat,
  TruthWindow,
  submitTruthBeats,
  submitTruthGap,
  truthWindows,
} from '../admin';
import { fetchStudyDetail } from '../api';
import { ReportComparison } from './ReportComparison';
import { fetchBeatMarkers } from '../review';
import { SignalWindow, loadSignalWindow } from '../signal';

/**
 * Beat and gap labelling (DESIGN.md §7.9, tier C).
 *
 * Unlike the blinded audit, **the engine's beats are shown** — the reader's job is to correct a
 * proposal into the true beat series, which is how a doctor labels thousands of beats. What keeps
 * this honest is that the reader's *whole final series* is stored, and "Save" is a deliberate act:
 * a window scrolled past is not a confirmation.
 *
 * Windows come from the server in the order they teach the most: long RR gaps first (a pause, or a
 * detector gone blind?), then the vendor's own flagged strips, then uniform-random controls, which
 * are what let the corrected windows be weighted back to the whole recording.
 *
 * Keys: N S V Q set the class of the selected beat · Delete removes it · ← → nudge it one sample
 * · click empty trace adds a beat at the nearest peak · click a marker selects it.
 */

interface Props {
  studyId: string;
  onBack: () => void;
}

const CLASS_COLOUR: Record<TruthBeat['cls'], string> = {
  N: '#34d399',
  S: '#fbbf24',
  V: '#fb7185',
  Q: '#94a3b8',
};
const CLASS_NAME: Record<TruthBeat['cls'], string> = {
  N: 'Normal',
  S: 'Supraventricular',
  V: 'Ventricular',
  Q: 'Other / unknown',
};
const VERDICTS: { value: GapVerdict; label: string; hint: string }[] = [
  { value: 'PAUSE', label: 'Pause', hint: 'No ventricular activity across the gap.' },
  { value: 'BEATS_PRESENT', label: 'Beats present', hint: 'Beats are there; the detector missed them.' },
  { value: 'NOT_ASSESSABLE', label: 'Not assessable', hint: 'The signal cannot say either way.' },
];
const REASON_TEXT: Record<TruthWindow['reason'], string> = {
  gap: 'Long RR gap',
  member_proposal: 'Model disagrees with the engine',
  vendor_strip: "Vendor-flagged strip",
  control: 'Random control',
};

const W = 1200;
const H = 240;
const PAD = 12;

const toClass = (label: string): TruthBeat['cls'] => (label === 'N' || label === 'S' || label === 'V' ? label : 'Q');

export const TruthLabeller: React.FC<Props> = ({ studyId, onBack }) => {
  const [windows, setWindows] = useState<TruthWindow[]>([]);
  const [idx, setIdx] = useState(0);
  const [rate, setRate] = useState(0);
  const [channel, setChannel] = useState('');
  const [signal, setSignal] = useState<SignalWindow | null>(null);
  const [beats, setBeats] = useState<TruthBeat[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [verdict, setVerdict] = useState<GapVerdict | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const current: TruthWindow | undefined = windows[idx];

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [detail, listed] = await Promise.all([fetchStudyDetail(studyId), truthWindows(studyId)]);
        if (!live) return;
        const first = detail.channels?.[0];
        setChannel(first?.label ?? '');
        setRate(first?.sample_rate_hz ?? 0);
        setWindows(listed.windows);
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Could not load windows.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [studyId]);

  // Load the trace and the engine's beats for the current window.
  useEffect(() => {
    if (!current || !rate || !channel) return;
    const ctrl = new AbortController();
    setSignal(null);
    setSelected(null);
    setVerdict(null);
    (async () => {
      try {
        const span = current.end_sample - current.start_sample;
        // canvas_px = span forces the raw (L0) trace: a labeller must never draw an envelope.
        const [win, markers] = await Promise.all([
          loadSignalWindow(studyId, channel, current.start_sample, span, span, ctrl.signal),
          fetchBeatMarkers(studyId, current.start_sample / rate, current.end_sample / rate, ctrl.signal),
        ]);
        setSignal(win);
        setBeats(
          markers
            .map((m) => ({ sample: Math.round(m.t * rate), cls: toClass(m.label) }))
            .filter((b) => b.sample >= current.start_sample && b.sample < current.end_sample),
        );
      } catch (e) {
        if (!ctrl.signal.aborted) setError(e instanceof Error ? e.message : 'Could not load the window.');
      }
    })();
    return () => ctrl.abort();
  }, [current, studyId, channel, rate]);

  const span = current ? current.end_sample - current.start_sample : 1;
  const x = useCallback((s: number) => PAD + ((s - (current?.start_sample ?? 0)) / span) * (W - 2 * PAD), [current, span]);

  const { path, lo, hi } = useMemo(() => {
    const s = signal?.samples;
    if (!s || s.length === 0) return { path: '', lo: 0, hi: 1 };
    let mn = Infinity;
    let mx = -Infinity;
    for (let i = 0; i < s.length; i += 1) {
      if (s[i] < mn) mn = s[i];
      if (s[i] > mx) mx = s[i];
    }
    const pad = (mx - mn) * 0.1 || 0.1;
    const a = mn - pad;
    const b = mx + pad;
    // Draw at most ~2 points per pixel; the raw samples stay what the marker positions refer to.
    const step = Math.max(1, Math.floor(s.length / (2 * (W - 2 * PAD))));
    const pts: string[] = [];
    for (let i = 0; i < s.length; i += step) {
      const px = PAD + (i / (s.length - 1 || 1)) * (W - 2 * PAD);
      const py = H - PAD - ((s[i] - a) / (b - a)) * (H - 2 * PAD);
      pts.push(`${px.toFixed(1)},${py.toFixed(1)}`);
    }
    return { path: `M${pts.join('L')}`, lo: a, hi: b };
  }, [signal]);

  const y = (mv: number) => H - PAD - ((mv - lo) / (hi - lo)) * (H - 2 * PAD);

  const addAt = (clientX: number) => {
    if (!current || !signal?.samples || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    const px = ((clientX - box.left) / box.width) * W;
    const centre = Math.round(current.start_sample + ((px - PAD) / (W - 2 * PAD)) * span);
    // Snap to the largest-magnitude sample within 60 ms, the same refinement the engine applies.
    const r = Math.round(0.06 * rate);
    let best = centre;
    let bestV = -1;
    const samples = signal.samples;
    for (let s = centre - r; s <= centre + r; s += 1) {
      const i = s - current.start_sample;
      if (i < 0 || i >= samples.length) continue;
      if (Math.abs(samples[i]) > bestV) {
        bestV = Math.abs(samples[i]);
        best = s;
      }
    }
    if (beats.some((b) => b.sample === best)) return;
    setBeats((bs) => [...bs, { sample: best, cls: 'N' as const }].sort((a, b) => a.sample - b.sample));
    setSelected(best);
  };

  const setClass = useCallback(
    (cls: TruthBeat['cls']) => setBeats((bs) => bs.map((b) => (b.sample === selected ? { ...b, cls } : b))),
    [selected],
  );
  const remove = useCallback(() => {
    setBeats((bs) => bs.filter((b) => b.sample !== selected));
    setSelected(null);
  }, [selected]);
  const nudge = useCallback(
    (d: number) => {
      if (selected === null || !current) return;
      const next = selected + d;
      if (next < current.start_sample || next >= current.end_sample || beats.some((b) => b.sample === next)) return;
      setBeats((bs) => bs.map((b) => (b.sample === selected ? { ...b, sample: next } : b)).sort((a, b) => a.sample - b.sample));
      setSelected(next);
    },
    [selected, current, beats],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      const k = e.key.toUpperCase();
      if (selected !== null && (k === 'N' || k === 'S' || k === 'V' || k === 'Q')) setClass(k);
      else if (selected !== null && (e.key === 'Delete' || e.key === 'Backspace')) remove();
      else if (e.key === 'ArrowLeft') nudge(-1);
      else if (e.key === 'ArrowRight') nudge(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, setClass, remove, nudge]);

  const needsVerdict = current?.reason === 'gap';
  const canSave = !!signal && !busy && (!needsVerdict || verdict !== null);

  const save = async () => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      await submitTruthBeats(studyId, {
        start_sample: current.start_sample,
        end_sample: current.end_sample,
        beats,
        reason: current.reason,
      });
      if (needsVerdict && current.gap && verdict) {
        await submitTruthGap(studyId, {
          start_sample: current.gap.start_sample,
          end_sample: current.gap.end_sample,
          verdict,
        });
      }
      setSaved((n) => n + 1);
      setIdx((i) => i + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the read.');
    } finally {
      setBusy(false);
    }
  };

  const seconds = (s: number) => (s / (rate || 1)).toFixed(1);

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-4 py-6">
      <div className="glass-panel flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">Beat and gap labelling</h2>
            <p className="text-xs text-slate-400">
              Correct the engine&apos;s beats into the true series. Your final series is what is stored.
            </p>
          </div>
        </div>
        <div className="text-xs tabular-nums text-slate-400">
          {windows.length > 0 && `${Math.min(idx + 1, windows.length)} / ${windows.length}`} · {saved} saved
        </div>
      </div>

      <ReportComparison studyId={studyId} />

      {error && <div className="rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">{error}</div>}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading windows…
        </div>
      )}

      {!loading && windows.length === 0 && !error && (
        <div className="glass-panel rounded-2xl p-6 text-sm text-slate-300">
          Nothing to read yet: this study has no stored analysis. Analyse it first — an empty list here means
          &quot;not analysed&quot;, never &quot;nothing to find&quot;.
        </div>
      )}

      {!loading && windows.length > 0 && !current && (
        <div className="glass-panel rounded-2xl p-6 text-sm text-emerald-200">
          All {windows.length} windows read. {saved} saved.
        </div>
      )}

      {current && (
        <div className="glass-panel space-y-3 rounded-2xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
            <span>
              <strong className="text-slate-200">{REASON_TEXT[current.reason]}</strong>
              {current.gap && <> · RR gap of {current.gap.seconds} s</>}
              {current.proposal && (
                <>
                  {' '}· {current.proposal.detector} proposes{' '}
                  <strong style={{ color: CLASS_COLOUR[toClass(current.proposal.proposed)] }}>
                    {CLASS_NAME[toClass(current.proposal.proposed)]}
                  </strong>{' '}
                  (p {current.proposal.confidence.toFixed(2)}); the engine said{' '}
                  {CLASS_NAME[toClass(current.proposal.engine)]}. Mark what the beat really is.
                </>
              )}
              {current.vendor?.description && <> · vendor: {current.vendor.description}</>}
            </span>
            <span className="font-mono tabular-nums">
              {seconds(current.start_sample)}–{seconds(current.end_sample)} s · {channel}
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              className="w-full min-w-[720px] cursor-crosshair"
              role="img"
              aria-label="ECG trace with the engine's beat marks; click to add a beat"
              onClick={(e) => addAt(e.clientX)}
            >
              {current.proposal && (
                <line
                  x1={x(current.proposal.sample)}
                  x2={x(current.proposal.sample)}
                  y1={PAD}
                  y2={H - PAD}
                  stroke={CLASS_COLOUR[toClass(current.proposal.proposed)]}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  opacity={0.8}
                />
              )}
              {current.gap && (
                <rect
                  x={x(current.gap.start_sample)}
                  y={0}
                  width={Math.max(2, x(current.gap.end_sample) - x(current.gap.start_sample))}
                  height={H}
                  fill="#f59e0b"
                  opacity={0.12}
                />
              )}
              <path d={path} fill="none" stroke="#cbd5e1" strokeWidth={1.2} />
              {beats.map((b) => {
                const i = b.sample - current.start_sample;
                const mv = signal?.samples?.[i] ?? 0;
                const on = b.sample === selected;
                return (
                  <g
                    key={b.sample}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(b.sample);
                    }}
                    className="cursor-pointer"
                  >
                    <line x1={x(b.sample)} x2={x(b.sample)} y1={PAD} y2={H - PAD} stroke={CLASS_COLOUR[b.cls]} strokeWidth={on ? 2.5 : 1} opacity={on ? 1 : 0.55} />
                    <circle cx={x(b.sample)} cy={y(mv)} r={on ? 6 : 4} fill={CLASS_COLOUR[b.cls]} />
                    <text x={x(b.sample)} y={PAD - 2} textAnchor="middle" fontSize={11} fill={CLASS_COLOUR[b.cls]}>
                      {b.cls}
                    </text>
                  </g>
                );
              })}
              {!signal && (
                <text x={W / 2} y={H / 2} textAnchor="middle" fill="#64748b" fontSize={14}>
                  loading trace…
                </text>
              )}
            </svg>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Selected beat:</span>
            {(Object.keys(CLASS_NAME) as TruthBeat['cls'][]).map((c) => (
              <button
                key={c}
                disabled={selected === null}
                onClick={() => setClass(c)}
                className="rounded-lg border border-slate-700 px-2 py-1 text-slate-300 hover:border-slate-500 disabled:opacity-30"
                style={{ borderLeft: `3px solid ${CLASS_COLOUR[c]}` }}
              >
                {c} · {CLASS_NAME[c]}
              </button>
            ))}
            <button
              disabled={selected === null}
              onClick={remove}
              className="flex items-center gap-1 rounded-lg border border-rose-900 px-2 py-1 text-rose-300 disabled:opacity-30"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
            <span className="ml-auto tabular-nums text-slate-500">{beats.length} beats</span>
          </div>

          {needsVerdict && (
            <div className="space-y-2 rounded-xl border border-amber-900/60 bg-amber-950/30 p-3">
              <div className="text-xs font-semibold text-amber-200">
                The shaded interval is {current.gap?.seconds} s with no detected beat. What is it?
              </div>
              <div className="flex flex-wrap gap-2">
                {VERDICTS.map((v) => (
                  <button
                    key={v.value}
                    onClick={() => setVerdict(v.value)}
                    title={v.hint}
                    className={`rounded-lg border px-3 py-1.5 text-xs ${
                      verdict === v.value ? 'border-amber-500 bg-amber-900 text-white' : 'border-slate-700 text-slate-300'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIdx((i) => i + 1)}
              className="rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-400 hover:text-white"
            >
              Skip (records nothing)
            </button>
            <button
              onClick={() => void save()}
              disabled={!canSave}
              className="flex items-center gap-1.5 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Reviewed — save and next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
